import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { BoxShadowSmall, Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import { CarouselScrollButton, Container, emblaHOC, Slide, Viewport } from 'components/emblaCarousel';
import { useQuickView } from 'components/quickView';
import { useOpenSurveyQuickView } from 'components/surveySystem/shared/hooks/useOpenSurveyQuickView';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { v4 as uuidv4 } from 'uuid';
import { breakpoint, sizes } from 'variables';
import SlideView from './components/slideView';
import ToolboxCard from './components/toolboxCard';
import { trackingEventNameMapper } from './shared/helper';
import { StyledQuickView } from './shared/styles';
import type { AdvisorToolboxItemWithId, IAdvisorToolbox, IAdvisorToolboxQuickView } from './shared/types';

const SliderWrapper = styled.div`
    position: relative;
    min-width: 0;
`;

const StyledPrev = styled(CarouselScrollButton)`
    display: none;

    ${breakpoint.up(sizes.sm)} {
        position: absolute;
        box-shadow: ${BoxShadowSmall};
        border: none;
        top: 50%;
        left: -${Spacing8};
        transform: translateY(-50%);
        display: block;
    }
`;

const StyledNext = styled(CarouselScrollButton)`
    display: none;

    ${breakpoint.up(sizes.sm)} {
        position: absolute;
        box-shadow: ${BoxShadowSmall};
        border: none;
        top: 50%;
        right: -${Spacing8};
        transform: translateY(-50%);
        display: block;
    }
`;

const StyledContainer = styled(Container)`
    display: flex;
`;

const StyledSlide = styled(Slide)<{ items: number }>`
    && {
        display: flex;
        flex: 1 0 200px;

        ${breakpoint.up(sizes.sm)} {
            max-width: none;
            flex: 0 0 50%;
        }
        ${({ items }) =>
            items >= 3 &&
            css`
                ${breakpoint.up(sizes.xl)} {
                    flex: 0 0 33.333%;
                }
            `}

        & + & {
            padding-left: ${Spacing8};
        }
    }
`;

const StyledViewport = styled(Viewport)`
    padding: 0 ${Spacing16};
    margin: 0 -${Spacing16};
`;

function ToolboxInline(props: IAdvisorToolbox) {
    const { model, productId, withNavigationButtons = true, className } = props;
    const { items } = model;
    const { toggle } = useQuickView('advisorToolbox');
    const [currentQuickView, setCurrentQuickView] = useState<IAdvisorToolboxQuickView>();
    const itemsWithId: AdvisorToolboxItemWithId[] = useMemo(() => items.map((x) => ({ ...x, id: uuidv4() })), [items]);
    const { openSurveyQuickViewBySurveyId } = useOpenSurveyQuickView();

    async function openQuickView(item: AdvisorToolboxItemWithId) {
        const { quickView, type, surveyId } = item;
        const eventName = trackingEventNameMapper(type);

        if (surveyId) {
            openSurveyQuickViewBySurveyId({ surveyId });
        }

        if (quickView) {
            setCurrentQuickView({ ...quickView, type, productId });

            if (eventName) {
                GA4Tracking({ eventName, context: 'advisor_toolbox', category: 'advisory' });
            }
        }
    }

    return (
        <>
            {currentQuickView && (
                <StyledQuickView quickViewId="advisorToolbox">
                    <SlideView quickView={currentQuickView} />
                </StyledQuickView>
            )}

            <SliderWrapper className={className}>
                <StyledViewport>
                    <StyledContainer>
                        {itemsWithId.map((item) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <StyledSlide items={itemsWithId.length} key={item.id}>
                                <ToolboxCard
                                    item={item}
                                    onClick={async () => {
                                        await openQuickView(item);
                                        toggle(true);
                                    }}
                                />
                            </StyledSlide>
                        ))}
                    </StyledContainer>
                </StyledViewport>
                {withNavigationButtons && <StyledPrev scrollType="previous" />}
                {withNavigationButtons && <StyledNext scrollType="next" />}
            </SliderWrapper>
        </>
    );
}

export default emblaHOC({ containScroll: 'trimSnaps', align: 'start', dragFree: true, slidesToScroll: 'auto' })(
    ToolboxInline
);
